import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, MotionConfig, useIsPresent, useReducedMotion } from "framer-motion";
import { ArrowRight, Mic, Square } from "lucide-react";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { Waveform } from "@/components/waveform";
import { cn } from "@/lib/utils";

const MIN_HEIGHT = 148;
const MAX_HEIGHT = 320;

/** Join two pieces of text the way a person would when adding a thought. */
function appendThought(base: string, addition: string, separator: "space" | "line"): string {
  const add = addition.trim();
  if (!add) return base;
  if (!base.trim()) return add;
  const trimmedBase = base.replace(/\s+$/, "");
  return separator === "line" ? `${trimmedBase}\n${add}` : `${trimmedBase} ${add}`;
}

function isTouchDevice(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(pointer: coarse)").matches || !window.matchMedia("(hover: hover)").matches;
}

interface FreeResponseComposerProps {
  value: string;
  onChange: (next: string) => void;
  /** Generated suggestions (legacy sessions may contain long ones). */
  suggestions?: string[] | null;
  continueLabel: string;
  /** Called with the final answer (any in-progress dictation already merged in). */
  onContinue: (finalAnswer: string) => void;
  placeholder?: string;
}

/**
 * Free-response-first answer composer for Unfolding questions.
 *
 * One text value is the answer. Suggestion chips only seed or extend that text;
 * dictation appends to it. Nothing about which chip was tapped leaves this component.
 */
export function FreeResponseComposer({
  value,
  onChange,
  suggestions,
  continueLabel,
  onContinue,
  placeholder = "What's coming up for you?",
}: FreeResponseComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const isPresent = useIsPresent();
  const isPresentRef = useRef(isPresent);
  isPresentRef.current = isPresent;
  const reduceMotion = useReducedMotion();

  // The text a chip produced, and what the field held before it — lets a second
  // chip replace an untouched first one without touching the user's own writing.
  const seedRef = useRef<{ base: string; result: string; chip: string } | null>(null);
  const [focused, setFocused] = useState(false);

  const voice = useVoiceInput({
    onTranscript: (text) => {
      if (!isPresentRef.current) return;
      seedRef.current = null;
      onChange(appendThought(valueRef.current, text, "space"));
    },
  });

  // Leaving this question (exit animation still running): stop listening now.
  useEffect(() => {
    if (!isPresent) voice.flush();
  }, [isPresent, voice.flush]);

  const chips = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of suggestions ?? []) {
      const s = typeof raw === "string" ? raw.trim() : "";
      if (!s || seen.has(s.toLowerCase())) continue;
      seen.add(s.toLowerCase());
      out.push(s);
      if (out.length === 4) break;
    }
    return out;
  }, [suggestions]);

  // Auto-grow between MIN_HEIGHT and MAX_HEIGHT, then scroll inside.
  const fitHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(Math.max(el.scrollHeight, MIN_HEIGHT), MAX_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
  };

  useLayoutEffect(fitHeight, [value]);

  // Re-measure when the width settles (first layout, rotation, font load).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let lastWidth = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== lastWidth) {
        lastWidth = el.clientWidth;
        fitHeight();
      }
    });
    ro.observe(el);
    void document.fonts?.ready.then(fitHeight);
    return () => ro.disconnect();
  }, []);

  // Desktop only: put the cursor in the field. On touch this would pop the keyboard.
  useEffect(() => {
    if (isTouchDevice()) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  // On touch, keep the field and Continue above the on-screen keyboard.
  useEffect(() => {
    if (!focused || !isTouchDevice()) return;
    const reveal = () =>
      actionsRef.current?.scrollIntoView({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
    const timer = window.setTimeout(reveal, 320);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", reveal);
    return () => {
      window.clearTimeout(timer);
      vv?.removeEventListener("resize", reveal);
    };
  }, [focused, reduceMotion]);

  const focusAtEnd = () => {
    const el = textareaRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.focus({ preventScroll: isTouchDevice() ? false : true });
      el.setSelectionRange(el.value.length, el.value.length);
      el.scrollTop = el.scrollHeight;
    });
  };

  const handleChip = (chip: string) => {
    const seed = seedRef.current;
    const untouchedSeed = seed && seed.result === value;

    // Tapping the chip that's currently seeded (and untouched) takes it back out.
    if (untouchedSeed && seed.chip === chip) {
      seedRef.current = null;
      onChange(seed.base);
      focusAtEnd();
      return;
    }

    const base = untouchedSeed ? seed.base : value;
    const next = appendThought(base, chip, "line");
    seedRef.current = { base, result: next, chip };
    onChange(next);
    focusAtEnd();
  };

  const trimmed = value.trim();
  const canContinue = trimmed.length > 0 || voice.interim.trim().length > 0;

  const handleContinue = () => {
    const pending = voice.isListening || voice.interim ? voice.flush() : "";
    const finalAnswer = appendThought(valueRef.current, pending, "space").trim();
    if (!finalAnswer) return;
    if (finalAnswer !== valueRef.current) onChange(finalAnswer);
    onContinue(finalAnswer);
  };

  const isChipActive = (chip: string) => {
    const seed = seedRef.current;
    if (seed && seed.result === value && seed.chip === chip) return true;
    return value.split("\n").some((line) => line.trim() === chip);
  };

  const showVoiceMessage = !!voice.error && voice.errorCode !== "unsupported";

  return (
    <MotionConfig reducedMotion="user">
      <div className="w-full flex flex-col">
        {chips.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-muted-foreground text-center mb-3">
              A few places you might start — or just write.
            </p>
            <div className="flex flex-wrap justify-center gap-2" role="group" aria-label="Suggestions to start from">
              {chips.map((chip, idx) => {
                const active = isChipActive(chip);
                return (
                  <motion.button
                    key={chip}
                    type="button"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 + idx * 0.04, duration: 0.25 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleChip(chip)}
                    aria-pressed={active}
                    title={chip}
                    className={cn(
                      "min-h-10 max-w-full px-3.5 py-2 rounded-2xl border text-sm text-left leading-snug",
                      "transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                      active
                        ? "bg-primary/10 border-primary/35 text-foreground"
                        : "bg-white/60 border-white/70 text-foreground/75 hover:bg-white/85 hover:text-foreground",
                    )}
                  >
                    <span className="line-clamp-2 break-words">{chip}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}

        {/* The response field — the hero of this step */}
        <div
          className={cn(
            "glass rounded-3xl shadow-sm transition-[box-shadow,background-color] duration-300",
            focused && "bg-white/75 shadow-[0_8px_30px_rgba(90,70,160,0.12)] ring-1 ring-primary/20",
          )}
        >
          <label htmlFor="free-response" className="sr-only">
            Your response
          </label>
          <textarea
            id="free-response"
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                if (canContinue) handleContinue();
              }
            }}
            placeholder={placeholder}
            rows={4}
            enterKeyHint="enter"
            className="block w-full bg-transparent px-5 pt-5 pb-2 text-base md:text-[17px] leading-relaxed text-foreground placeholder:text-muted-foreground/60 focus:outline-none resize-none break-words [overflow-wrap:anywhere]"
            style={{ minHeight: MIN_HEIGHT }}
          />

          <div className="flex items-end justify-between gap-3 pl-5 pr-2 pb-2 min-h-[52px]">
            <div className="min-w-0 flex-1 pb-3" aria-live="polite">
              {voice.isListening ? (
                <div className="flex items-center gap-2 min-w-0">
                  <Waveform active className="h-4 shrink-0" />
                  <p className="text-sm text-muted-foreground truncate">
                    {voice.interim ? <span className="text-foreground/55">{voice.interim}</span> : "Listening…"}
                  </p>
                </div>
              ) : null}
            </div>

            {voice.isSupported && (
              <div className="relative shrink-0">
                {voice.isListening && <span aria-hidden className="listen-ring absolute inset-0 rounded-full" />}
                <button
                  type="button"
                  onClick={() => {
                    voice.clearError();
                    if (!voice.isListening) seedRef.current = null;
                    voice.toggle();
                  }}
                  aria-pressed={voice.isListening}
                  aria-label={voice.isListening ? "Stop dictation" : "Dictate your response"}
                  title={voice.isListening ? "Stop dictation" : "Speak your response"}
                  className={cn(
                    "relative w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-200",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    voice.isListening
                      ? "bg-primary text-white shadow-sm"
                      : "bg-white/70 text-muted-foreground hover:text-primary hover:bg-white border border-white/80",
                  )}
                >
                  {voice.isListening ? <Square className="w-3.5 h-3.5 fill-current" /> : <Mic className="w-[18px] h-[18px]" />}
                </button>
              </div>
            )}
          </div>
        </div>

        {showVoiceMessage && (
          <p className="text-xs text-muted-foreground mt-2 px-2" role="status">
            {voice.error}
          </p>
        )}

        <div
          ref={actionsRef}
          className="flex items-center justify-between gap-3 mt-4 pb-[max(env(safe-area-inset-bottom),0px)] scroll-mb-6"
        >
          <p className="hidden md:block text-[11px] text-muted-foreground/60">
            {canContinue ? "⌘/Ctrl + Enter to continue" : ""}
          </p>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 min-h-12 rounded-full bg-primary text-white font-medium text-sm transition-all hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {continueLabel} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </MotionConfig>
  );
}
